import { RichText } from '@seek/cmsu-rich-text';
import {
  Bleed,
  Box,
  ContentBlock,
  Divider,
  Heading,
  Inline,
  Stack,
  Text,
} from 'braid-design-system';
import he from 'he';
import React from 'react';

import { richTextRenderers } from '../components/modules';

import { PageLayout } from '../components/PageLayout/PageLayout';
import {
  Breadcrumbs,
  CategoryLink,
  SocialMediaShare,
  SubscriptionForm,
} from '@seek/cmsu-components';
import type { LayoutProps } from '../shared/types';

import { useConfig } from '../shared/hooks';
import { transformSeoData } from 'src/helpers/seo';
import { useLoaderData } from 'react-router';
import type { ArticleLocalization } from 'src/helpers/loadersHelper';
import { useTargetBlank } from '../shared/hooks/useTargetBlank';
import { WPT_SALESFORCE_API } from '../../config';
import { usePageTracking } from '../shared/hooks/usePageTracking';
import { CMSUProvider } from '@seek/cmsu-components/src/hooks/useCMSUContext';
import { prefixUrlWithLanguage } from '@seek/cmsu-components/src/helpers/prefixUrlWithLanguage';

const layoutProps: LayoutProps = {
  title: 'Loading...',
};

// The Hygraph site that the article should be fetched from
type Props = {
  site: string;
  section: string;
};

/**
 * TODO:
 * 2. add SEO meta data in <head>
 */
export const Article = ({ section }: Props) => {
  useTargetBlank();
  // Get the params from context.
  const { melwaysLocale, fullURL, sourceName, privacyUrl, country, language } =
    useConfig();

  // Get the article content by using the loader.
  const data = useLoaderData() as ArticleLocalization & {
    locales: {
      locale: string;
      url: string;
    }[];
  };

  const {
    featureImage,
    category: articleCategory,
    content,
    title,
    seo,
    timeToRead,
    locale,
  } = data;

  let contentText = he.decode(content.text.trim().replace(/\\n/g, ' '));
  contentText = contentText.trim().replace(/\n/g, '').replace(/  /g, '');
  const imageUrl = featureImage[0]?.url;
  const pageSeo = transformSeoData(seo || {}, {
    title,
    image: imageUrl,
    description:
      contentText.substring(0, contentText.indexOf('.') + 1) || contentText,
  });
  const imageText = featureImage[0]?.alt;
  const categoryName = articleCategory?.name || '';
  const categorySlug = articleCategory?.slug || '';
  const parentCategoryName = articleCategory?.parent?.name || '';
  const parentCategorySlug = articleCategory?.parent?.slug;
  const rawContent = content.raw;
  const embedReferences = content.references;
  const description = seo?.description || title;
  const items = [
    {
      text: parentCategoryName,
      url: prefixUrlWithLanguage({
        url: `/${section}/${parentCategorySlug}`,
        locale,
      }),
    },
    {
      text: categoryName,
      url: prefixUrlWithLanguage({
        url: `/${section}/category/${categorySlug}`,
        locale,
      }),
    },
  ];

  usePageTracking(title.toLocaleLowerCase(), categorySlug);

  return (
    <PageLayout
      layoutProps={{
        ...layoutProps,
        title,
        section: { sectionName: section },
        seo: pageSeo,
        locales: data.locales,
      }}
    >
      <CMSUProvider config={{ language }}>
        <Box background="surface">
          <ContentBlock width="medium">
            <Stack space="none">
              <Breadcrumbs items={items} />
              <Box
                background="surface"
                padding="large"
                borderRadius={{
                  mobile: 'none',
                  desktop: 'large',
                }}
                overflow="hidden"
                component="article"
              >
                <Stack space="gutter">
                  {Boolean(imageUrl) && (
                    <Bleed horizontal="large" top="large">
                      <Box background="brand">
                        <img
                          src={imageUrl}
                          alt={imageText || title}
                          style={{
                            display: 'block',
                            width: '100%',
                          }}
                        />
                      </Box>
                    </Bleed>
                  )}
                  <Inline
                    space={{
                      mobile: 'small',
                      tablet: 'medium',
                    }}
                    alignY={'center'}
                  >
                    <CategoryLink
                      name={categoryName}
                      slug={categorySlug}
                      basePath={section}
                      locale={locale}
                    />
                    {Boolean(timeToRead) && (
                      <Text tone="secondary">{`${timeToRead} min read`}</Text>
                    )}
                  </Inline>
                  <Heading level="1">{title}</Heading>
                  <SocialMediaShare
                    pageUrl={fullURL}
                    title={title}
                    summary={description}
                  />
                  <RichText
                    content={rawContent}
                    renderers={richTextRenderers}
                    references={embedReferences}
                  />
                  <Divider weight="strong" />
                  <SubscriptionForm
                    type={section}
                    locale={melwaysLocale}
                    sourceName={sourceName}
                    privacyUrl={privacyUrl}
                    wptSalesForceApi={WPT_SALESFORCE_API}
                    country={country}
                  />
                </Stack>
              </Box>
            </Stack>
          </ContentBlock>
        </Box>
      </CMSUProvider>
    </PageLayout>
  );
};
